import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import BalanceVisibilityToggle from '../../components/BalanceVisibilityToggle';
import MaskedValue from '../../components/MaskedValue';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [balanceView, setBalanceView] = useState('all');

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const fetchOverview = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await api.get(`/dashboard/overview/${user.id}`);
        setOverview(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Não foi possível carregar os dados do painel.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [user?.id]);

  const balanceData = useMemo(() => {
    const balances = overview?.balances;
    if (!balances) return null;

    const formatBRL = (value) =>
      new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        Number(value ?? 0)
      );

    const formatBTC = (value) =>
      new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 8, maximumFractionDigits: 8 }).format(
        Number(value ?? 0)
      );

    const toPercent = (value) => {
      const numeric = Number(value ?? 0);
      return {
        numeric,
        formatted: `${Number.isFinite(numeric) ? numeric.toFixed(2) : '0'}%`,
      };
    };

    const raw = balances.raw ?? {};
    const percentages = balances.percentages ?? {};

    const realPercent = toPercent(percentages.real);
    const bitcoinPercent = toPercent(percentages.bitcoin);

    return {
      total: { raw: raw.total ?? 0, formatted: formatBRL(raw.total) },
      real: { raw: raw.real ?? 0, formatted: formatBRL(raw.real) },
      realDistribution: { raw: raw.realDistribution ?? 0, formatted: formatBRL(raw.realDistribution) },
      bitcoin: { raw: raw.bitcoinAmount ?? 0, formatted: formatBTC(raw.bitcoinAmount) },
      bitcoinFiat: { raw: raw.bitcoinReal ?? 0, formatted: formatBRL(raw.bitcoinReal) },
      bitcoinDistribution: { raw: raw.bitcoinDistribution ?? 0, formatted: formatBRL(raw.bitcoinDistribution) },
      percentages: {
        real: realPercent.numeric,
        realFormatted: realPercent.formatted,
        bitcoin: bitcoinPercent.numeric,
        bitcoinFormatted: bitcoinPercent.formatted,
      },
    };
  }, [overview]);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="dashboard" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12 relative">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="hidden lg:block">
              <h2 className="text-2xl font-semibold text-slate-900">
                {user?.name ? (
                  <>
                    Bem-vindo{' '}
                    <span className="font-bold text-3xl leading-tight text-slate-900">{user.name}</span>
                  </>
                ) : (
                  'Bem-vindo ao painel'
                )}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <BalanceVisibilityToggle />
              <InternalTopMenu onLogout={logout} />
            </div>
          </header>

          {loading && <div className="text-sm text-gray-500">Carregando informações...</div>}

          {error ? (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
          ) : (
            overview && (
              <>
                <section className="mb-8">
                  <div className="rounded-2xl border border-slate-100 bg-white px-6 py-6 shadow-sm flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-sm text-gray-400">Preço atual</div>
                      <a
                        href="/transact"
                        className="inline-flex items-center gap-2 rounded-full bg-tBlue text-white px-4 py-2 text-xs font-semibold hover:bg-tBlue2 transition-colors"
                      >
                        Comprar Bitcoin <i className="bi-arrow-right-short text-lg"></i>
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center text-sm text-gray-500 gap-2">
                        <i className="bi-currency-bitcoin bg-[#F79319] text-white rounded-full px-[5px] py-[2px]" />
                        1 BTC =
                      </div>
                      <div className="text-3xl font-semibold text-slate-900">
                        {overview?.bitcoinPrice?.formatted || '—'}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mb-8">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="text-slate-900 text-sm font-semibold">Saldo disponível</div>
                    <a
                      href="/transactions"
                      className="text-sm font-semibold text-slate-900 flex items-center gap-1 mt-2 md:mt-0"
                    >
                      Ver extrato <i className="bi-arrow-right-short text-tOrange text-2xl"></i>
                    </a>
                  </div>
                  <hr className="my-4" />

                  <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setBalanceView('all')}
                          className={`text-xs font-medium px-4 py-2 rounded-full transition-colors ${
                            balanceView === 'all'
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          Todas as contas
                        </button>
                        <button
                          type="button"
                          onClick={() => setBalanceView('real')}
                          className={`text-xs font-medium px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${
                            balanceView === 'real'
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          <img
                            src="https://static.vecteezy.com/system/resources/previews/011/419/619/original/brazil-flag-file-free-png.png"
                            alt="Bandeira Brasil"
                            className="w-4"
                          />
                          Real
                        </button>
                        <button
                          type="button"
                          onClick={() => setBalanceView('bitcoin')}
                          className={`text-xs font-medium px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${
                            balanceView === 'bitcoin'
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          <i className="bi-currency-bitcoin bg-[#F79319] text-white rounded-full px-[5px] py-[2px]" />
                          Bitcoin
                        </button>
                      </div>

                      <div className="mt-6 rounded-lg border border-slate-100 bg-white px-6 py-5 shadow-sm">
                        {balanceView === 'all' && (
                          <>
                        <div className="text-xs text-gray-400">Saldo consolidado</div>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="text-gray-400">R$</div>
                          <div className="text-4xl font-semibold text-slate-900">
                                <MaskedValue>{balanceData?.total.formatted || '0,00'}</MaskedValue>
                          </div>
                        </div>

                        <div className="mt-6 text-xs text-gray-400">
                          <div className="mb-3">Alocação</div>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                              <i className="bi-circle-fill text-[6px] text-green-600"></i>
                              <span className="text-green-600">
                                    {balanceData?.percentages.realFormatted || '0%'} - Reais (
                                    <MaskedValue>{balanceData?.realDistribution.formatted || '0,00'}</MaskedValue>)
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <i className="bi-circle-fill text-[6px] text-[#F7931A]"></i>
                              <span className="text-[#F7931A]">
                                    {balanceData?.percentages.bitcoinFormatted || '0%'} - Bitcoin (
                                    <MaskedValue>{balanceData?.bitcoinDistribution.formatted || '0,00'}</MaskedValue>)
                              </span>
                            </div>
                          </div>
                          <div className="h-1 bg-slate-200 mt-2 flex overflow-hidden rounded">
                            <div
                              className="bg-green-600 h-full transition-all"
                                  style={{
                                    width: `${balanceData?.percentages.real ?? 0}%`,
                                  }}
                            ></div>
                            <div
                              className="bg-[#F7931A] h-full transition-all"
                                  style={{
                                    width: `${balanceData?.percentages.bitcoin ?? 0}%`,
                                  }}
                            ></div>
                          </div>
                        </div>
                          </>
                        )}

                        {balanceView === 'real' && (
                          <>
                            <div className="text-xs text-gray-400">Saldo em Reais</div>
                            <div className="flex items-center gap-2 mt-2">
                              <div className="text-gray-400">R$</div>
                              <div className="text-4xl font-semibold text-slate-900">
                                <MaskedValue>{balanceData?.real.formatted || '0,00'}</MaskedValue>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-4">
                              Disponível em Reais (inclui saldos de todas as contas na moeda BRL).
                            </div>
                          </>
                        )}

                        {balanceView === 'bitcoin' && (
                          <>
                            <div className="text-xs text-gray-400">Saldo em Bitcoin</div>
                            <div className="flex items-center gap-2 mt-2">
                              <div className="text-4xl font-semibold text-slate-900">
                                <MaskedValue>{balanceData?.bitcoin.formatted || '0,00000000'}</MaskedValue> BTC
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-4">
                              ~ <MaskedValue>{balanceData?.bitcoinFiat.formatted || '0,00'}</MaskedValue> em Reais.
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {balanceView === 'all' && (
                    <div className="rounded-lg border border-slate-100 bg-white px-6 py-5 shadow-sm">
                      <div className="text-xs text-gray-400">Saldo em Bitcoin</div>
                      <div className="mt-3 text-slate-900 text-2xl font-semibold">
                          <MaskedValue>{balanceData?.bitcoin.formatted || '0,00000000'}</MaskedValue> BTC
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                          ~ <MaskedValue>{balanceData?.bitcoinFiat.formatted || '0,00'}</MaskedValue> em Reais
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                <section>
                  <h3 className="text-base font-semibold text-slate-900 tracking-wide">Atalhos</h3>
                  <hr className="my-4" />
                  <div className="grid gap-4 md:grid-cols-3">
                    {(overview.shortcuts || []).map((shortcut) => (
                      <a
                        href={shortcut.href}
                        key={shortcut.id}
                        className="rounded-2xl border border-transparent bg-gradient-to-br from-[#EEF6FF] via-[#E3EDFF] to-[#D7E5FF] px-4 py-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#0255F1]/40 flex items-center gap-4"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#0255F1] text-xl shadow-inner">
                          <img src={shortcut.icon} alt={shortcut.label} className="w-6 h-6 object-contain" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[#012B87]">{shortcut.label}</div>
                          <div className="text-xs text-slate-500 mt-1">Acesse esta ação rapidamente</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              </>
            )
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
